import React,{useState,useMemo,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';
import {imageService} from '../utils/imageService.js';
import Header from './Header.jsx';

const {FiFilter,FiCheck,FiX,FiArrowLeft,FiAlertCircle,FiInfo}=FiIcons;

const Results=({matches,onBack,onBackToHome})=> {
  const [selectedBreed,setSelectedBreed]=useState(null);
  const [breedImages,setBreedImages]=useState({});
  const [loadingImages,setLoadingImages]=useState(true);
  const [filters,setFilters]=useState({size: 'all',energyLevel: 'all',groomingNeeds: 'all',goodWithKids: 'all',sortBy: 'score'});
  const [showFilters,setShowFilters]=useState(false);

  // Load images for all breeds
  useEffect(()=> {
    let isMounted=true;
    const loadBreedImages=async ()=> {
      setLoadingImages(true);
      const imageMap={};
      // Process all matches
      for (const match of matches) {
        try {
          const photo=await imageService.getBreedPhoto(match.breed.id);
          if (isMounted) {
            imageMap[match.breed.id]=photo;
          }
        } catch (error) {
          console.error(`Error loading image for ${match.breed.id}`,error);
        }
      }
      if (isMounted) {
        setBreedImages(imageMap);
        setLoadingImages(false);
      }
    };

    if (matches.length > 0) {
      loadBreedImages();
    }

    return ()=> {
      isMounted=false;
    };
  },[matches]);

  const filteredAndSortedMatches=useMemo(()=> {
    let filtered=matches.filter(match=> {
      const breed=match.breed;
      if (filters.size !=='all' && breed.size !==filters.size) return false;
      if (filters.energyLevel !=='all' && breed.energyLevel !==filters.energyLevel) return false;
      if (filters.groomingNeeds !=='all' && breed.groomingNeeds !==filters.groomingNeeds) return false;
      if (filters.goodWithKids !=='all' && breed.goodWithKids !==filters.goodWithKids) return false;
      return true;
    });

    // Sort by selected criteria
    filtered.sort((a,b)=> {
      switch (filters.sortBy) {
        case 'score': return b.score - a.score;
        case 'size':
          const sizeOrder=['Extra Small','Small','Medium','Large','Extra Large'];
          return sizeOrder.indexOf(a.breed.size) - sizeOrder.indexOf(b.breed.size);
        case 'energy':
          const energyOrder=['Low','Low-Medium','Medium','Medium-High','High','Very High'];
          return energyOrder.indexOf(a.breed.energyLevel) - energyOrder.indexOf(b.breed.energyLevel);
        case 'grooming':
          const groomingOrder=['Low','Medium','High'];
          return groomingOrder.indexOf(a.breed.groomingNeeds) - groomingOrder.indexOf(b.breed.groomingNeeds);
        default: return b.score - a.score;
      }
    });

    return filtered;
  },[matches,filters]);

  const getScoreColor=(score)=> {
    if (score >=80) return 'text-green-600 bg-green-100'; // High match
    if (score >=60) return 'text-[#F2674C] bg-[#F2674C]/10'; // Medium match
    return 'text-gray-600 bg-gray-100'; // Low match
  };

  // New helper for the badge ensuring high contrast
  const getScoreBadgeStyle=(score)=> {
      if (score >= 80) return 'bg-white text-green-600 border-2 border-green-100';
      if (score >= 60) return 'bg-white text-[#F2674C] border-2 border-[#F2674C]/20';
      return 'bg-white text-gray-500 border-2 border-gray-100';
  };

  const getScoreLabel=(score)=> {
    if (score >=80) return 'Excellent Match';
    if (score >=60) return 'Good Match';
    if (score >=40) return 'Fair Match';
    return 'Poor Match';
  };

  // Helper to handle image errors
  const handleImageError=(e)=> {
    e.target.onerror=null;
    e.target.src='FetchMate_Hero.png';
  };

  const BreedCard=({match,index})=> {
    const breedImage=breedImages[match.breed.id];
    return (
      <motion.div
        initial={{opacity: 0,y: 40}}
        animate={{opacity: 1,y: 0}}
        transition={{delay: index * 0.1,duration: 0.6,ease: [0.25,0.25,0,1]}}
        className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-500 hover:scale-105"
      >
        <div className="relative">
          {loadingImages ? (
            <div className="w-full h-64 bg-gray-200 animate-pulse flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            </div>
          ) : (
            <img
              src={breedImage?.medium || breedImage?.url || 'FetchMate_Hero.png'}
              alt={breedImage?.alt || match.breed.name}
              className="w-full h-64 object-cover"
              loading="lazy"
              onError={handleImageError}
            />
          )}
          {/* UPDATED: High visibility score badge */}
          <div className="absolute top-4 right-4 z-10">
            <span className={`px-4 py-2 rounded-2xl text-lg font-extrabold shadow-md ${getScoreBadgeStyle(match.score)}`}>
              {Math.round(match.score)}%
            </span>
          </div>
          <div className="absolute top-4 left-4 z-10">
            <span className="px-3 py-1 bg-black/70 backdrop-blur-sm text-white text-sm rounded-xl font-semibold">
              #{index + 1}
            </span>
          </div>
        </div>
        <div className="p-8">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-[28px] font-bold text-[#282f3a] leading-tight">{match.breed.name}</h3>
            <span className="text-sm text-[#282f3a] font-medium">{match.breed.category}</span>
          </div>
          <div className="flex items-center mb-6">
            <span className={`px-3 py-1 rounded-xl text-sm font-bold ${getScoreColor(match.score)}`}>
              {getScoreLabel(match.score)}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div className="flex flex-col items-start gap-1">
              <span className="text-[#282f3a] font-medium">Size:</span>
              <span className="font-bold text-[#282f3a]">{match.breed.size}</span>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-[#282f3a] font-medium">Energy:</span>
              <span className="font-bold text-[#282f3a]">{match.breed.energyLevel}</span>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-[#282f3a] font-medium">Grooming:</span>
              <span className="font-bold text-[#282f3a]">{match.breed.groomingNeeds}</span>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-[#282f3a] font-medium">Training:</span>
              <span className="font-bold text-[#282f3a]">{match.breed.trainingDifficulty}</span>
            </div>
          </div>

          {/* Top Bonuses */}
          {match.bonuses.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-bold text-green-700 mb-3">Why this breed fits:</h4>
              <div className="space-y-2">
                {match.bonuses.slice(0,2).map((bonus,i)=> (
                  <div key={i} className="flex items-center text-sm text-green-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="font-medium">{bonus}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={()=> setSelectedBreed(match)}
            className="w-full bg-[#F2674C] text-white py-4 px-6 rounded-2xl font-bold hover:bg-[#e85a3e] transition-all duration-300 hover:scale-105 shadow-lg shadow-[#F2674C]/25"
          >
            View Details
          </button>
        </div>
      </motion.div>
    );
  };

  const BreedModal=({match,onClose})=> {
    const breedImage=breedImages[match.breed.id];
    return (
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{scale: 0.9,opacity: 0}}
          animate={{scale: 1,opacity: 1}}
          exit={{scale: 0.9,opacity: 0}}
          className="bg-white rounded-3xl max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e)=> e.stopPropagation()}
        >
          <div className="relative">
            <img
              src={breedImage?.url || breedImage?.medium || 'FetchMate_Hero.png'}
              alt={breedImage?.alt || match.breed.name}
              className="w-full h-80 object-cover rounded-t-3xl"
              onError={handleImageError}
            />
            <button
              onClick={onClose}
              className="absolute top-6 right-6 bg-white bg-opacity-90 p-3 rounded-full hover:bg-opacity-100 transition-all shadow-lg"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
            <div className="absolute bottom-6 left-6 bg-black bg-opacity-60 text-white px-6 py-4 rounded-2xl backdrop-blur-sm">
              <h2 className="text-3xl font-bold">{match.breed.name}</h2>
              <p className="text-sm opacity-90 font-medium">{match.breed.category} • {getScoreLabel(match.score)}</p>
            </div>
          </div>

          <div className="p-8">
            {/* Score and Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-24 h-20 rounded-3xl text-3xl font-bold ${getScoreColor(match.score)}`}>
                  {Math.round(match.score)}%
                </div>
                <p className="text-sm text-[#282f3a] mt-3 font-medium">Compatibility Score</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#282f3a]">{match.breed.weight}</div>
                <p className="text-sm text-[#282f3a] font-medium">Weight</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#282f3a]">{match.breed.lifespan}</div>
                <p className="text-sm text-[#282f3a] font-medium">Lifespan</p>
              </div>
            </div>

            {/* Characteristics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                {label: 'Size',value: match.breed.size},
                {label: 'Energy Level',value: match.breed.energyLevel},
                {label: 'Grooming',value: match.breed.groomingNeeds},
                {label: 'Training',value: match.breed.trainingDifficulty},
                {label: 'Good with Kids',value: match.breed.goodWithKids},
                {label: 'Good with Pets',value: match.breed.goodWithPets},
                {label: 'Barking',value: match.breed.barking},
                {label: 'Shedding',value: match.breed.shedding}
              ].map((item,i)=> (
                <div key={i} className="bg-gray-50 p-4 rounded-2xl">
                  <p className="text-xs text-gray-500 mb-2 font-medium">{item.label}</p>
                  <p className="font-bold text-[#282f3a]">{item.value}</p>
                </div>
              ))}
            </div>

            {/* Personality Traits */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#282f3a]">Personality Traits</h3>
              <div className="flex flex-wrap gap-3">
                {match.breed.personality.map((trait,i)=> (
                  <span key={i} className="px-4 py-2 bg-[#f3ede6] text-[#282f3a] rounded-2xl text-sm font-semibold">
                    {trait}
                  </span>
                ))}
              </div>
            </div>

            {/* Why This Breed Fits */}
            {match.bonuses.length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4 text-green-700">Why This Breed Fits You</h3>
                <div className="space-y-3">
                  {match.bonuses.map((bonus,i)=> (
                    <div key={i} className="flex items-center text-green-600">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 mr-3 flex-shrink-0" />
                      <span className="font-medium">{bonus}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Potential Concerns */}
            {match.penalties.length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4 text-[#F2674C]">Things to Consider</h3>
                <div className="space-y-3">
                  {match.penalties.map((penalty,i)=> (
                    <div key={i} className="flex items-center text-[#F2674C]">
                      <SafeIcon icon={FiInfo} className="w-5 h-5 mr-3 flex-shrink-0" />
                      <span className="font-medium">{penalty}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Health Considerations */}
            {match.breed.commonIssues && match.breed.commonIssues.length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4 text-[#282f3a]">Common Health Considerations</h3>
                <div className="space-y-2">
                  {match.breed.commonIssues.map((issue,i)=> (
                    <div key={i} className="text-[#282f3a] font-medium">
                      • {issue}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo Credit */}
            {breedImage && breedImage.photographer && (
              <div className="text-center pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Image source: {breedImage.photographer}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f3ede6] to-white">
      <Header onBackToHome={onBackToHome} showLogo={true} showHomeButton={true} />
      
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="flex items-center text-[#282f3a] hover:text-[#F2674C] mr-6 font-semibold transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
              Back to Assessment
            </button>
            <div>
              <h1 className="text-4xl font-bold text-[#282f3a]">Your Perfect Dog Matches</h1>
              <p className="text-[#282f3a] font-medium">Found {filteredAndSortedMatches.length} compatible breeds</p>
            </div>
          </div>
          
          <button
            onClick={()=> setShowFilters(!showFilters)}
            className="flex items-center px-6 py-3 bg-white border border-gray-300 rounded-2xl hover:bg-gray-50 font-semibold transition-all shadow-sm"
          >
            <SafeIcon icon={FiFilter} className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{opacity: 0,height: 0}}
              animate={{opacity: 1,height: 'auto'}}
              exit={{opacity: 0,height: 0}}
              className="bg-white rounded-3xl p-8 mb-12 shadow-lg border border-gray-100"
            >
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div>
                  <label className="block text-sm font-bold text-[#282f3a] mb-3">Size</label>
                  <select
                    value={filters.size}
                    onChange={(e)=> setFilters({...filters,size: e.target.value})}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 font-semibold focus:ring-2 focus:ring-[#F2674C] focus:border-transparent"
                  >
                    <option value="all">All Sizes</option>
                    <option value="Extra Small">Extra Small</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Large">Large</option>
                    <option value="Extra Large">Extra Large</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#282f3a] mb-3">Energy Level</label>
                  <select
                    value={filters.energyLevel}
                    onChange={(e)=> setFilters({...filters,energyLevel: e.target.value})}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 font-semibold focus:ring-2 focus:ring-[#F2674C] focus:border-transparent"
                  >
                    <option value="all">All Energy Levels</option>
                    <option value="Low">Low</option>
                    <option value="Low-Medium">Low-Medium</option>
                    <option value="Medium">Medium</option>
                    <option value="Medium-High">Medium-High</option>
                    <option value="High">High</option>
                    <option value="Very High">Very High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#282f3a] mb-3">Grooming</label>
                  <select
                    value={filters.groomingNeeds}
                    onChange={(e)=> setFilters({...filters,groomingNeeds: e.target.value})}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 font-semibold focus:ring-2 focus:ring-[#F2674C] focus:border-transparent"
                  >
                    <option value="all">All Grooming Levels</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#282f3a] mb-3">Good with Kids</label>
                  <select
                    value={filters.goodWithKids}
                    onChange={(e)=> setFilters({...filters,goodWithKids: e.target.value})}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 font-semibold focus:ring-2 focus:ring-[#F2674C] focus:border-transparent"
                  >
                    <option value="all">All</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#282f3a] mb-3">Sort By</label>
                  <select
                    value={filters.sortBy}
                    onChange={(e)=> setFilters({...filters,sortBy: e.target.value})}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 font-semibold focus:ring-2 focus:ring-[#F2674C] focus:border-transparent"
                  >
                    <option value="score">Best Match</option>
                    <option value="size">Size</option>
                    <option value="energy">Energy Level</option>
                    <option value="grooming">Grooming Needs</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAndSortedMatches.map((match,index)=> (
            <BreedCard key={match.breed.id} match={match} index={index} />
          ))}
        </div>

        {filteredAndSortedMatches.length===0 && (
          <div className="text-center py-20">
            <p className="text-[#282f3a] text-xl font-medium mb-6">No breeds match your current filters.</p>
            <button
              onClick={()=> setFilters({size: 'all',energyLevel: 'all',groomingNeeds: 'all',goodWithKids: 'all',sortBy: 'score'})}
              className="px-8 py-4 bg-[#F2674C] text-white rounded-2xl hover:bg-[#e85a3e] font-bold transition-all shadow-lg shadow-[#F2674C]/25"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Disclaimer Banner - Moved to bottom */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.5}}
          className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mt-16 mb-8 max-w-4xl mx-auto"
        >
          <div className="flex items-start space-x-4">
            <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-blue-900 mb-2">Important Disclaimer</h3>
              <p className="text-blue-800 text-sm leading-relaxed">
                These results are educational recommendations based on your responses and should not replace professional advice from veterinarians,certified dog trainers,or breed experts. Every dog is unique,and individual temperament can vary significantly within breeds. We strongly recommend meeting potential pets in person,researching reputable breeders or shelters,and consulting with professionals before making any adoption decisions.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Breed Detail Modal */}
      <AnimatePresence>
        {selectedBreed && (
          <BreedModal match={selectedBreed} onClose={()=> setSelectedBreed(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Results;